import React, { useState } from 'react';
import { toast } from 'sonner';
import { HardHat, Loader2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../ui/dialog';
import { haccpApi, erroreHaccp } from '../../haccpApi';
import { LavoratoreDpi } from './LavoratoreDpi';

const Azienda = ({ az }) => (!az ? null : (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-700 space-y-0.5" data-testid="dpi-azienda">
    <p className="font-semibold text-gray-900">{az.ragione_sociale || 'Ragione sociale non trovata nel DVR'}</p>
    <p>{[az.indirizzo, az.cap, az.citta].filter(Boolean).join(' ')}</p>
    <p>{[az.partita_iva && `P.IVA ${az.partita_iva}`, az.datore_di_lavoro && `Datore di lavoro: ${az.datore_di_lavoro}`].filter(Boolean).join(' · ')}</p>
    <p className="text-[11px] text-gray-500">Dati presi dal DVR.</p>
  </div>
));

const Stato = ({ d, onLeggi, onRicarica }) => {
  if (d.errore_rete) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-center justify-between gap-2" data-testid="dpi-errore-rete">
        <p className="text-sm text-red-800">Non riesco a caricare i verbali: controlla la connessione.</p>
        <button onClick={onRicarica} className="h-9 px-3 rounded-lg bg-white border border-red-300 text-xs font-semibold text-red-800 flex items-center gap-1" data-testid="dpi-ricarica"><RefreshCw className="h-3.5 w-3.5" />Riprova</button>
      </div>
    );
  }
  if (!d.dvr) return <p className="text-sm text-gray-700 rounded-xl border border-gray-200 p-3" data-testid="dpi-senza-dvr">Carica prima il DVR in Documenti (Sicurezza sul lavoro): dati della società e DPI si prendono da lì.</p>;
  if (d.in_corso) return <p className="text-sm text-gray-700 flex items-center gap-2" data-testid="dpi-lettura"><Loader2 className="h-4 w-4 animate-spin" />Leggo il DVR e gli attestati: può volerci qualche minuto.</p>;
  if (!d.errore) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-center justify-between gap-2" data-testid="dpi-errore">
      <p className="text-sm text-red-800">{d.errore}</p>
      <button onClick={onLeggi} className="h-9 px-3 rounded-lg bg-white border border-red-300 text-xs font-semibold text-red-800 flex items-center gap-1" data-testid="dpi-riprova"><RefreshCw className="h-3.5 w-3.5" />Riprova</button>
    </div>
  );
};

export const VerbaliDpiDialog = ({ aperto, dpi, onClose, onFirma, onCambiato }) => {
  const [busy, setBusy] = useState(false);
  const d = dpi.dati;
  const leggi = () => dpi.leggi().catch((e) => toast.error(erroreHaccp(e, 'Lettura non avviata')));
  const crea = async (ids) => {
    setBusy(true);
    try {
      const r = await haccpApi.post('/documenti/dpi/crea', { persona_ids: ids });
      toast.success(r.creati.length > 1 ? `Creati ${r.creati.length} verbali: ora falli firmare` : `Verbale di ${r.creati[0]} creato: ora fallo firmare`);
      onCambiato();
    } catch (e) { toast.error(erroreHaccp(e, 'Verbali non creati')); } finally { setBusy(false); }
  };
  const daCreare = d?.conta?.da_creare || 0;
  return (
    <Dialog open={aperto} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[96vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="dpi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6"><HardHat className="h-5 w-5 text-orange-600" />Verbali di consegna DPI</DialogTitle>
          <DialogDescription>Uno per dipendente: società e DPI dal DVR, mansione dagli attestati. Firmano il lavoratore e il datore di lavoro; la data di consegna la scrivi quando firmi.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto" /> : (
          <div className="space-y-3">
            <Stato d={d} onLeggi={leggi} onRicarica={dpi.ricarica} />
            <Azienda az={d.azienda} />
            <div className="space-y-2">
              {(d.dvr ? d.lavoratori : []).map((r) => <LavoratoreDpi key={r.persona_id} r={r} busy={busy} onCrea={crea} onFirma={onFirma} onSalvato={dpi.ricarica} />)}
            </div>
            {daCreare > 1 && (
              <button onClick={() => crea(null)} disabled={busy} data-testid="dpi-crea-tutti"
                className="w-full h-12 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}Crea tutti ({daCreare})
              </button>
            )}
            <p className="text-[11px] text-gray-500">Controlla ogni verbale prima di firmarlo: i DPI sono quelli scritti nel tuo DVR. Il verbale firmato resta in Documenti, alla voce "Verbale di consegna DPI".</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
